/**
 * Kundenportal-Tracking-Client (Phase 2)
 *
 * - Batched Events (max. 50 / Request)
 * - Flush alle 5s oder bei pagehide via sendBeacon
 * - Respektiert Do-Not-Track (navigator.doNotTrack) und lokalen Opt-Out
 * - Single-instance Singleton
 */

export type TrackingEventType = 'page_view' | 'click' | 'scroll' | 'time_on_page' | 'custom';

export interface TrackingEvent {
  type: TrackingEventType;
  path?: string;
  target?: string;
  value?: number;
  metadata?: Record<string, unknown>;
  clientTs?: number;
}

interface TrackingClientOpts {
  endpoint?: string;
  flushIntervalMs?: number;
  maxBatchSize?: number;
}

const DEFAULT_ENDPOINT = '/api/customer/tracking';
const DEFAULT_FLUSH_INTERVAL = 5_000;
const DEFAULT_MAX_BATCH = 50;
const OPT_OUT_STORAGE_KEY = 'portalTrackingOptOut';

function readCsrfCookie(): string | null {
  if (typeof document === 'undefined') return null;
  const m = document.cookie.match(/(?:^|;\s*)customer_csrf=([^;]+)/);
  return m ? decodeURIComponent(m[1]) : null;
}

function browserDntEnabled(): boolean {
  if (typeof navigator === 'undefined') return false;
  const nav = navigator as Navigator & { doNotTrack?: string; msDoNotTrack?: string };
  const dnt = nav.doNotTrack ?? nav.msDoNotTrack;
  return dnt === '1' || dnt === 'yes';
}

function localOptOut(): boolean {
  if (typeof localStorage === 'undefined') return false;
  try {
    return localStorage.getItem(OPT_OUT_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

class TrackingClient {
  private readonly endpoint: string;
  private readonly flushIntervalMs: number;
  private readonly maxBatchSize: number;
  private queue: TrackingEvent[] = [];
  private timer: ReturnType<typeof setInterval> | null = null;
  private started = false;

  constructor(opts: TrackingClientOpts = {}) {
    this.endpoint = opts.endpoint ?? DEFAULT_ENDPOINT;
    this.flushIntervalMs = opts.flushIntervalMs ?? DEFAULT_FLUSH_INTERVAL;
    this.maxBatchSize = opts.maxBatchSize ?? DEFAULT_MAX_BATCH;
  }

  /** Startet Periodic-Flush + pagehide-Beacon. Idempotent. */
  start(): void {
    if (this.started || typeof window === 'undefined') return;
    this.started = true;
    this.timer = setInterval(() => void this.flush(false), this.flushIntervalMs);
    window.addEventListener('pagehide', () => void this.flush(true));
    window.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') void this.flush(true);
    });
  }

  stop(): void {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
    this.started = false;
  }

  /** Event in die Queue legen. Wird stumm verworfen bei DNT / Opt-Out. */
  track(event: TrackingEvent): void {
    if (this.isDisabled()) return;
    this.queue.push({ ...event, clientTs: event.clientTs ?? Date.now() });
    if (this.queue.length >= this.maxBatchSize) {
      void this.flush(false);
    }
  }

  /** Opt-Out setzen (verhindert alle kuenftigen Tracking-Calls). */
  setOptOut(optedOut: boolean): void {
    try {
      if (optedOut) localStorage.setItem(OPT_OUT_STORAGE_KEY, '1');
      else localStorage.removeItem(OPT_OUT_STORAGE_KEY);
    } catch { /* ignore */ }
    if (optedOut) this.queue = [];
  }

  isDisabled(): boolean {
    return browserDntEnabled() || localOptOut();
  }

  /** Sendet den aktuellen Queue-Inhalt. Bei useBeacon=true via sendBeacon (keine async-Wartung moeglich). */
  async flush(useBeacon: boolean): Promise<void> {
    if (this.queue.length === 0) return;
    const batch = this.queue.splice(0, this.maxBatchSize);
    const body = JSON.stringify({ events: batch });
    const csrf = readCsrfCookie();

    if (useBeacon && typeof navigator !== 'undefined' && 'sendBeacon' in navigator) {
      // sendBeacon limitation: no custom headers possible, CSRF via cookie only.
      // Backend accepts CSRF via double-submit cookie (Header preferred but unavailable).
      // sendBeacon has no XHR interface → fire-and-forget, no error feedback.
      try {
        const blob = new Blob([body], { type: 'application/json' });
        navigator.sendBeacon(this.endpoint, blob);
      } catch {
        // sendBeacon failure is non-fatal; events silently dropped
      }
      return;
    }

    try {
      await fetch(this.endpoint, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(csrf ? { 'X-CSRF-Token': csrf } : {}),
        },
        body,
        keepalive: true,
      });
    } catch {
      // Events werden bei Fehler verworfen (keine Retry-Logik → waere zu teuer)
    }
  }
}

let singleton: TrackingClient | null = null;

export function getTrackingClient(): TrackingClient {
  if (!singleton) singleton = new TrackingClient();
  return singleton;
}

export function trackEvent(event: TrackingEvent): void {
  getTrackingClient().track(event);
}

/** Nur in Tests wirksam (sauberer Singleton-Reset zwischen Testfällen). */
export function __resetTrackingClient(): void {
  if (process.env.NODE_ENV !== 'test') return;
  singleton = null;
}
